import { useCallback } from 'react'
import { ConfirmationResult, signInWithPhoneNumber } from 'firebase/auth'
import { getFirAuth } from '../../Api/firebase/firebaseConnect'
import { executeWithRetry, withTimeout, AUTH_TIMEOUT } from '../../Utils'
import { isNavigatorOnline } from '../../Utils/networking/networkHelpers'
import { handleAuthError } from '../../Utils'
import { ANALYTICS_TAGS } from '../../Constant'
import type { PhoneAuthOperations } from './types'
import { useRecaptcha } from './useRecaptcha'

export const usePhoneAuth = (
  toggleShowSnackbar: (snackbarData: {
    text: string
    color: 'red' | 'blue' | 'green'
  }) => void,
  shouldInitializeRecaptcha: boolean = false
): PhoneAuthOperations => {
  const recaptchaVerifier = useRecaptcha('phone-signin-btn', shouldInitializeRecaptcha)

  const sendPhoneVerification = useCallback(
    async (phoneNumber: string): Promise<ConfirmationResult | null> => {
      try {
        if (!phoneNumber || phoneNumber.trim() === '') {
          handleAuthError(
            ANALYTICS_TAGS.login_error,
            'Invalid phone number',
            'sendPhoneVerification',
            {
              provider: 'phone',
            }
          )
          toggleShowSnackbar({
            text: 'Please enter a valid phone number.',
            color: 'red',
          })
          return null
        }

        if (!isNavigatorOnline()) {
          toggleShowSnackbar({
            text: 'Network connection required. Please check your connection.',
            color: 'red',
          })
          return null
        }

        const auth = await withTimeout(
          getFirAuth(),
          AUTH_TIMEOUT,
          'getFirAuth for phone verification'
        )

        if (!auth) {
          handleAuthError(
            ANALYTICS_TAGS.auth_change_error,
            'Firebase auth not available',
            'sendPhoneVerification'
          )
          toggleShowSnackbar({
            text: 'Phone service unavailable. Please try again later.',
            color: 'red',
          })
          return null
        }

        const phoneVerificationWithRetry = async (): Promise<ConfirmationResult> => {
          if (!recaptchaVerifier) {
            throw new Error('Failed to create phone verification reCAPTCHA')
          }

          return await withTimeout(
            signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier as any),
            AUTH_TIMEOUT,
            'signInWithPhoneNumber'
          )
        }

        const confirmationResult = await executeWithRetry(
          phoneVerificationWithRetry,
          `sendPhoneVerification_${phoneNumber}`
        )

        return confirmationResult || null
      } catch (err) {
        handleAuthError(ANALYTICS_TAGS.login_error, err, 'sendPhoneVerification', {
          provider: 'phone',
        })
        toggleShowSnackbar({
          text: 'Failed to send verification code. Please try again.',
          color: 'red',
        })
        return null
      }
    },
    [recaptchaVerifier]
  )

  return {
    sendPhoneVerification,
  }
}
